import { useState, useMemo, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePortalContracting } from '@/hooks/useContratos'
import { useAllServicos } from '@/hooks/useServicos'
import { formatCurrency } from '@/lib/formatters'
import {
    CheckCircle2, Loader2, ArrowRight, ArrowLeft, ScrollText,
    CreditCard, X, AlertCircle, Heart, Layers, Plus, Minus, ListPlus,
    Star, Sparkles, ShoppingBag, Tag, Info
} from 'lucide-react'
import type { Servico, ServicoPlano, ServicoAdicional, CrossSellItem } from '@/types/database'
import { usePortalStore } from '@/stores/portal.store'
import { supabase } from '@/config/supabase'

/* Pro-rata calculation helper */
function calcProRata(valorMensal: number): number {
    const today = new Date()
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
    const remainingDays = daysInMonth - today.getDate() + 1
    return Math.round((valorMensal / daysInMonth) * remainingDays * 100) / 100
}

/* ============================================================
   ContractingModal — Exported multi-step flow
   ============================================================ */

type ModalStep = 'details' | 'planos' | 'adicionais' | 'crosssell' | 'terms' | 'summary' | 'result'

export function ContractingModal({ servico, onClose }: {
    servico: Servico
    onClose: () => void
}) {
    const navigate = useNavigate()
    const associado = usePortalStore(s => s.associado)
    const portalContracting = usePortalContracting()
    const { data: allServicos } = useAllServicos()
    const cfg = servico.config_contratacao
    const hasTermo = !!cfg?.termo_aceite
    const hasAdesao = !!cfg?.gerar_adesao

    /* Planos and Adicionais */
    const activePlanos = useMemo(() => (servico.planos || []).filter(p => p.ativo), [servico.planos])
    const activeAdicionais = useMemo(() => (servico.adicionais || []).filter(a => a.ativo), [servico.adicionais])
    const hasPlanos = activePlanos.length > 0
    const hasAdicionais = activeAdicionais.length > 0

    const [selectedPlano, setSelectedPlano] = useState<ServicoPlano | null>(
        hasPlanos ? (activePlanos.find(p => p.destaque) || activePlanos[0] || null) : null
    )
    const [adicionalQtds, setAdicionalQtds] = useState<Record<string, number>>(() => {
        const init: Record<string, number> = {}
        activeAdicionais.forEach(a => { init[a.id] = 0 })
        return init
    })

    /* Build cross-sell offers */
    const crossSellOffers = useMemo(() => {
        if (!cfg?.cross_sell?.habilitado || !cfg.cross_sell.servicos?.length) return []
        return cfg.cross_sell.servicos
            .map(cs => {
                const svc = allServicos?.find(s => s.id === cs.servico_id)
                if (!svc || !svc.ativo) return null
                const desconto = cs.desconto_percentual
                    ? svc.valor_mensal * (1 - cs.desconto_percentual / 100)
                    : cs.desconto_fixo
                        ? Math.max(0, svc.valor_mensal - cs.desconto_fixo)
                        : svc.valor_mensal
                return {
                    servico: svc,
                    item: cs,
                    valorComDesconto: Math.round(desconto * 100) / 100,
                    temDesconto: desconto < svc.valor_mensal
                }
            })
            .filter(Boolean) as { servico: Servico; item: CrossSellItem; valorComDesconto: number; temDesconto: boolean }[]
    }, [cfg?.cross_sell, allServicos])

    const [selectedCrossSells, setSelectedCrossSells] = useState<string[]>([])

    /* Determine step order */
    const steps: ModalStep[] = useMemo(() => {
        const s: ModalStep[] = ['details']
        if (hasPlanos) s.push('planos')
        if (hasAdicionais) s.push('adicionais')
        if (crossSellOffers.length > 0) s.push('crosssell')
        if (hasTermo) s.push('terms')
        s.push('summary')
        s.push('result')
        return s
    }, [hasTermo, hasPlanos, hasAdicionais, crossSellOffers.length])

    const [currentIdx, setCurrentIdx] = useState(0)
    const [diaVencimento, setDiaVencimento] = useState<number>(10)
    const step = steps[currentIdx]!

    /* Term acceptance */
    const [termoAceito, setTermoAceito] = useState(false)
    const [scrolledToBottom, setScrolledToBottom] = useState(false)
    const termsRef = useRef<HTMLDivElement>(null)

    /* Check scroll on mount/step change */
    useEffect(() => {
        if (step === 'terms' && termsRef.current) {
            const el = termsRef.current
            /* If content fits without scroll, mark as read immediately */
            if (el.scrollHeight <= el.clientHeight + 10) {
                setScrolledToBottom(true)
            }
        }
    }, [step])

    /* Result state */
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [resultSuccess, setResultSuccess] = useState(false)
    const [createdContratoId, setCreatedContratoId] = useState<string | null>(null)

    /* Effective pricing */
    const valorMensalEfetivo = selectedPlano ? selectedPlano.valor_mensal : servico.valor_mensal
    
    // Monthly Math
    const valorAdicionaisMensalTotal = useMemo(() => {
        let total = 0
        activeAdicionais.forEach(a => {
            const qty = adicionalQtds[a.id] || 0
            const cobrado = Math.max(0, qty - a.incluso_no_plano)
            total += cobrado * a.valor_unitario
        })
        return total
    }, [adicionalQtds, activeAdicionais])

    const valorCrossSellMensalTotal = useMemo(() => {
        return crossSellOffers
            .filter(o => selectedCrossSells.includes(o.servico.id))
            .reduce((acc, o) => acc + o.valorComDesconto, 0)
    }, [crossSellOffers, selectedCrossSells])

    const valorMensalFinal = valorMensalEfetivo + valorAdicionaisMensalTotal + valorCrossSellMensalTotal

    // Adhesion Math
    const valorAdesaoBase = useMemo(() => {
        if (!hasAdesao) return 0
        if (selectedPlano?.valor_adesao != null) return selectedPlano.valor_adesao
        if (cfg?.adesao_pro_rata) return calcProRata(valorMensalEfetivo)
        return cfg?.valor_adesao_fixo || valorMensalEfetivo
    }, [hasAdesao, cfg, valorMensalEfetivo, selectedPlano])

    const valorAdicionaisAdesaoTotal = useMemo(() => {
        let total = 0
        activeAdicionais.forEach(a => {
            if (a.valor_adesao != null && a.valor_adesao > 0) {
                const qty = adicionalQtds[a.id] || 0
                total += qty * a.valor_adesao
            }
        })
        return total
    }, [adicionalQtds, activeAdicionais])

    const valorCrossSellAdesaoTotal = useMemo(() => {
        return crossSellOffers
            .filter(o => selectedCrossSells.includes(o.servico.id))
            .reduce((acc, o) => {
                const csCfg = o.servico.config_contratacao
                if (!csCfg?.gerar_adesao) return acc
                const adesao = csCfg.valor_adesao_fixo || o.servico.valor_mensal
                return acc + adesao
            }, 0)
    }, [crossSellOffers, selectedCrossSells])

    const valorAdesaoFinal = valorAdesaoBase + valorAdicionaisAdesaoTotal + valorCrossSellAdesaoTotal
    const hasAdesaoFinal = valorAdesaoFinal > 0

    const next = () => setCurrentIdx(i => Math.min(i + 1, steps.length - 1))
    const prev = () => setCurrentIdx(i => Math.max(i - 1, 0))

    /* Handle contract submission using RPC */
    const handleConfirm = async () => {
        if (!associado) {
            alert('Sessão inválida. Faça login novamente.')
            return
        }

        setIsSubmitting(true)
        try {
            // Geração de Hash ICP-Brasil (Simulado via SHA-256 no client para rapidez, ou Edge Function se preferir)
            // Aqui vamos montar o manifesto de assinatura
            const timestamp = new Date().toISOString()
            const userAgent = navigator.userAgent
            const documentContent = servico.termos_adesao || cfg?.termo_aceite || 'Termo de adesão padrão.'
            
            // Cria um payload para assinatura
            const manifestoStr = `${associado.cpf}|${timestamp}|${documentContent}`
            
            // Gera hash SHA-256 simples no navegador para registro
            const msgUint8 = new TextEncoder().encode(manifestoStr)
            const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8)
            const hashArray = Array.from(new Uint8Array(hashBuffer))
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

            const result = await portalContracting.mutateAsync({
                tenant_id: associado.tenant_id,
                associado_id: associado.id,
                servico_id: servico.id,
                servico_ids: [servico.id, ...selectedCrossSells],
                dia_vencimento: diaVencimento,
                valor_mensal: valorMensalFinal,
                valor_adesao: valorAdesaoFinal,
                adesao_pro_rata: !!cfg?.adesao_pro_rata,
                termo_aceite_em: timestamp,
                assinatura_digital: {
                    hash: hashHex,
                    timestamp,
                    user_agent: userAgent,
                    documento_base: documentContent,
                    ip_estimado: 'Aguardando registro server-side'
                },
                ...(selectedPlano ? { plano_id: selectedPlano.id, plano_nome: selectedPlano.nome } : {}),
                ...(Object.keys(adicionalQtds).some(k => adicionalQtds[k]! > 0) ? {
                    adicionais: activeAdicionais
                        .filter(a => (adicionalQtds[a.id] || 0) > 0)
                        .map(a => ({
                            adicional_id: a.id,
                            nome: a.nome,
                            quantidade: adicionalQtds[a.id],
                            valor_unitario: a.valor_unitario,
                        }))
                } : {}),
            } as any)

            setCreatedContratoId(result.contrato_id)
            setResultSuccess(true)

            // Atribuição Automática de Pontos Sempre Juntos (Adesão)
            if (servico.pontos_adesao && servico.pontos_adesao > 0) {
                try {
                    await supabase
                        .schema('veicular_system')
                        .from('veic_pontos_extrato')
                        .insert({
                            tenant_id: associado.tenant_id,
                            associado_id: associado.id,
                            contrato_id: result.contrato_id,
                            pontos: servico.pontos_adesao,
                            descricao: `Adesão ao serviço: ${servico.nome}`,
                            tipo: 'contratacao'
                        })
                } catch (pErr) {
                    console.error('Erro ao atribuir pontos de adesão', pErr)
                    // Não bloqueia o sucesso da contratação se apenas os pontos falharem
                }
            }

            next()
        } catch (err: any) {
            console.error('Erro na contratação portal:', err)
            alert('Erro ao contratar: ' + (err.message || 'Tente novamente.'))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleTermScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
            setScrolledToBottom(true)
        }
    }

    /* Step indicator */
    const displaySteps = steps.filter(s => s !== 'result')
    const stepLabels: Record<ModalStep, string> = {
        details: 'Detalhes',
        planos: 'Plano',
        adicionais: 'Adicionais',
        crosssell: 'Ofertas',
        terms: 'Aceite',
        summary: 'Confirmação',
        result: 'Resultado',
    }

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in shadow-2xl">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-lg sm:mx-4 max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-5 pb-3 border-b border-surface-100">
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-primary-600 uppercase tracking-wider">
                            {step !== 'result' ? `Passo ${currentIdx + 1} de ${displaySteps.length}` : 'Concluído'}
                        </p>
                        <h3 className="text-lg font-bold text-surface-900 mt-0.5 truncate">{servico.nome}</h3>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-surface-100 transition-colors -mr-1 outline-none">
                        <X size={20} className="text-surface-700/60" />
                    </button>
                </div>

                {/* Step indicator dots */}
                {step !== 'result' && (
                    <div className="flex items-center gap-2 px-5 pt-3">
                        {displaySteps.map((s, i) => (
                            <div key={s} className="flex items-center gap-2">
                                <div className={`flex items-center gap-1.5 text-xs font-medium transition-colors
                                    ${i <= currentIdx ? 'text-primary-600' : 'text-surface-700/30'}`}>
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold
                                        ${i < currentIdx ? 'bg-primary-600 text-white' :
                                            i === currentIdx ? 'bg-primary-100 text-primary-600 ring-2 ring-primary-600' :
                                                'bg-surface-100 text-surface-700/40'}`}>
                                        {i < currentIdx ? '✓' : i + 1}
                                    </div>
                                    <span className="hidden sm:inline">{stepLabels[s]}</span>
                                </div>
                                {i < displaySteps.length - 1 && (
                                    <div className={`w-6 h-0.5 rounded ${i < currentIdx ? 'bg-primary-600' : 'bg-surface-100'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {step === 'details' && (
                        <StepDetails
                            servico={servico}
                            valorAdesao={valorAdesaoBase}
                            hasAdesao={hasAdesao}
                            diaVencimento={diaVencimento}
                            onDiaChange={setDiaVencimento}
                        />
                    )}
                    {step === 'planos' && (
                        <StepPlanos
                            planos={activePlanos}
                            selected={selectedPlano}
                            onSelect={setSelectedPlano}
                        />
                    )}
                    {step === 'adicionais' && (
                        <StepAdicionais
                            adicionais={activeAdicionais}
                            qtds={adicionalQtds}
                            onQtdChange={(id, qty) => setAdicionalQtds(prev => ({ ...prev, [id]: qty }))}
                        />
                    )}
                    {step === 'crosssell' && (
                        <StepCrossSell 
                            offers={crossSellOffers}
                            selected={selectedCrossSells}
                            onToggle={(id) => {
                                setSelectedCrossSells(prev => 
                                    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                                )
                            }}
                        />
                    )}
                    {step === 'terms' && (
                        <StepTerms
                            termsRef={termsRef}
                            termo={servico.termos_adesao || cfg?.termo_aceite || 'Carregando termos...'}
                            aceito={termoAceito}
                            onAceitoChange={setTermoAceito}
                            scrolledToBottom={scrolledToBottom}
                            onScroll={handleTermScroll}
                        />
                    )}
                    {step === 'summary' && (
                        <StepSummary
                            servico={servico}
                            valorAdesaoBase={valorAdesaoBase}
                            valorAdesaoFinal={valorAdesaoFinal}
                            hasAdesao={hasAdesao}
                            hasAdesaoFinal={hasAdesaoFinal}
                            diaVencimento={diaVencimento}
                            selectedPlano={selectedPlano}
                            adicionais={activeAdicionais}
                            adicionalQtds={adicionalQtds}
                            crossSellOffers={crossSellOffers.filter(o => selectedCrossSells.includes(o.servico.id))}
                            valorMensalFinal={valorMensalFinal}
                        />
                    )}
                    {step === 'result' && (
                        <StepResult
                            servico={servico}
                            hasAdesaoFinal={hasAdesaoFinal}
                            valorAdesaoFinal={valorAdesaoFinal}
                            success={resultSuccess}
                        />
                    )}
                </div>

                {/* Footer actions */}
                <div className="p-5 pt-3 border-t border-surface-100 flex gap-3">
                    {step === 'details' && (
                        <>
                            <button onClick={onClose} className="btn flex-1 border border-surface-200 hover:bg-surface-50">
                                Cancelar
                            </button>
                            <button onClick={next} className="btn btn-primary flex-1 gap-2">
                                Próximo <ArrowRight size={16} />
                            </button>
                        </>
                    )}
                    {(step === 'planos' || step === 'adicionais' || step === 'crosssell') && (
                        <>
                            <button onClick={prev} className="btn flex-1 border border-surface-200 hover:bg-surface-50 gap-2">
                                <ArrowLeft size={16} /> Voltar
                            </button>
                            <button onClick={next} className="btn btn-primary flex-1 gap-2"
                                disabled={step === 'planos' && !selectedPlano}>
                                Próximo <ArrowRight size={16} />
                            </button>
                        </>
                    )}
                    {step === 'terms' && (
                        <>
                            <button onClick={prev} className="btn flex-1 border border-surface-200 hover:bg-surface-50 gap-2">
                                <ArrowLeft size={16} /> Voltar
                            </button>
                            <button
                                onClick={next}
                                disabled={!termoAceito}
                                className="btn btn-primary flex-1 gap-2 disabled:opacity-50"
                            >
                                Aceitar e Continuar <ArrowRight size={16} />
                            </button>
                        </>
                    )}
                    {step === 'summary' && (
                        <>
                            <button onClick={prev} className="btn flex-1 border border-surface-200 hover:bg-surface-50 gap-2">
                                <ArrowLeft size={16} /> Voltar
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={isSubmitting}
                                className="btn btn-primary flex-1 gap-2"
                            >
                                {isSubmitting ? (
                                    <><Loader2 size={16} className="animate-spin" /> Processando...</>
                                ) : (
                                    <>Confirmar Contratação <CheckCircle2 size={16} /></>
                                )}
                            </button>
                        </>
                    )}
                    {step === 'result' && (
                        <button 
                            onClick={() => {
                                onClose()
                                if (createdContratoId) navigate(`/portal/contratos/${createdContratoId}`)
                            }} 
                            className="btn btn-primary flex-1 gap-2"
                        >
                            Ver Meus Contratos <ArrowRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

/* ============================================================
   Step Components
   ============================================================ */

function StepDetails({ servico, valorAdesao, hasAdesao, diaVencimento, onDiaChange }: {
    servico: Servico; valorAdesao: number; hasAdesao: boolean; diaVencimento: number; onDiaChange: (v: number) => void
}) {
    return (
        <div className="space-y-4">
            <p className="text-sm text-surface-700/70 leading-relaxed">{servico.descricao || 'Sem descrição disponível.'}</p>

            <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-xl bg-surface-50">
                    <span className="text-sm text-surface-700/70">Mensalidade</span>
                    <span className="font-bold text-primary-600 text-lg">{formatCurrency(servico.valor_mensal)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-surface-50">
                    <span className="text-sm text-surface-700/70">Carência</span>
                    <span className="font-medium text-surface-900">
                        {servico.carencia_dias > 0 ? `${servico.carencia_dias} dias` : 'Sem carência'}
                    </span>
                </div>
                {hasAdesao && (
                    <div className="flex items-center justify-between p-3 rounded-xl bg-amber-50 border border-amber-100">
                        <span className="text-sm text-amber-800">Taxa de adesão</span>
                        <span className="font-bold text-amber-700">{formatCurrency(valorAdesao)}</span>
                    </div>
                )}
                <div className="p-3 rounded-xl bg-surface-50">
                    <span className="text-xs text-surface-700/60 block mb-2 uppercase font-bold tracking-wider">Vencimento da mensalidade</span>
                    <div className="flex gap-2">
                        {[10, 20].map(dia => (
                            <button
                                key={dia}
                                onClick={() => onDiaChange(dia)}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all
                                    ${diaVencimento === dia
                                        ? 'bg-primary-600 text-white shadow-md'
                                        : 'bg-white text-surface-700 border border-surface-200 hover:border-primary-200'}`}
                            >
                                Dia {dia}
                            </button>
                        ))}
                    </div>
                </div>

                {servico.config_contratacao?.ativacao_via_api && (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                        <AlertCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-800">A ativação deste serviço depende de processamento e pode levar até 24h após confirmação do pagamento.</p>
                    </div>
                )}

                {!!servico.pontos_adesao && servico.pontos_adesao > 0 && (
                    <div className="flex items-center justify-between p-3 rounded-xl bg-rose-50 border border-rose-100">
                        <span className="text-sm text-rose-800 font-bold flex items-center gap-1.5">
                           <Heart size={14} className="fill-current text-rose-500" /> Bônus Sempre Juntos
                        </span>
                        <span className="font-black text-rose-600">+{servico.pontos_adesao} PONTOS</span>
                    </div>
                )}
            </div>
        </div>
    )
}

/* ─── Step: Seleção de Plano ─── */
function StepPlanos({ planos, selected, onSelect }: {
    planos: ServicoPlano[]; selected: ServicoPlano | null; onSelect: (p: ServicoPlano) => void
}) {
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-surface-700/70">
                <Layers size={18} className="text-indigo-600" />
                <span>Escolha o plano ideal para você</span>
            </div>

            <div className="space-y-3">
                {planos.map(p => {
                    const isSelected = selected?.id === p.id
                    return (
                        <button
                            key={p.id}
                            onClick={() => onSelect(p)}
                            className={`w-full text-left p-4 rounded-2xl border-2 transition-all relative overflow-hidden
                                ${isSelected
                                    ? 'border-primary-500 bg-primary-50/50 shadow-md shadow-primary-100'
                                    : 'border-surface-200 bg-white hover:border-primary-200 hover:shadow-sm'}`}
                        >
                            {p.destaque && (
                                <div className="absolute top-2 right-2">
                                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-black flex items-center gap-1">
                                        <Star size={9} fill="currentColor" /> Recomendado
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                                        ${isSelected ? 'border-primary-600 bg-primary-600' : 'border-surface-300'}`}>
                                        {isSelected && <CheckCircle2 size={12} className="text-white" />}
                                    </div>
                                    <h4 className="font-bold text-surface-900">{p.nome}</h4>
                                </div>
                                <span className="font-black text-primary-600 text-lg">{formatCurrency(p.valor_mensal)}<span className="text-xs font-medium text-surface-400">/mês</span></span>
                            </div>
                            {p.descricao && <p className="text-xs text-surface-500 ml-8 mb-2">{p.descricao}</p>}
                            {p.inclusoes && p.inclusoes.length > 0 && (
                                <div className="ml-8 space-y-1">
                                    {p.inclusoes.map((inc, i) => (
                                        <div key={i} className="flex items-center gap-1.5 text-xs text-surface-600">
                                            <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                                            <span>{inc.titulo}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

/* ─── Step: Adicionais ─── */
function StepAdicionais({ adicionais, qtds, onQtdChange }: {
    adicionais: ServicoAdicional[]; qtds: Record<string, number>
    onQtdChange: (id: string, qty: number) => void
}) {
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-surface-700/70">
                <ListPlus size={18} className="text-teal-600" />
                <span>Personalize seu plano</span>
            </div>
            <p className="text-xs text-surface-400">Adicione itens opcionais ao seu serviço. Cada item pode ter um custo adicional mensal.</p>

            <div className="space-y-3">
                {adicionais.map(a => {
                    const qty = qtds[a.id] || 0
                    const cobrado = Math.max(0, qty - a.incluso_no_plano)
                    const custoExtra = cobrado * a.valor_unitario

                    return (
                        <div key={a.id} className="p-4 rounded-2xl bg-white border border-surface-200 space-y-3">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0 pr-3">
                                    <p className="text-sm font-bold text-surface-900">{a.nome}</p>
                                    {a.descricao && <p className="text-xs text-surface-500 mt-0.5">{a.descricao}</p>}
                                    <div className="flex flex-wrap gap-2 mt-1.5">
                                        <span className="text-[10px] font-bold text-surface-400 bg-surface-50 px-2 py-0.5 rounded-full">
                                            {formatCurrency(a.valor_unitario)}/un
                                        </span>
                                        {a.incluso_no_plano > 0 && (
                                            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                                {a.incluso_no_plano} incluso(s)
                                            </span>
                                        )}
                                        <span className="text-[10px] font-bold text-surface-400 bg-surface-50 px-2 py-0.5 rounded-full">
                                            Máx: {a.max_quantidade}
                                        </span>
                                    </div>
                                </div>

                                {a.tipo === 'toggle' ? (
                                    <button
                                        onClick={() => onQtdChange(a.id, qty > 0 ? 0 : 1)}
                                        className={`w-14 h-8 rounded-full transition-all flex items-center px-1
                                            ${qty > 0 ? 'bg-primary-600 justify-end' : 'bg-surface-200 justify-start'}`}
                                    >
                                        <div className="w-6 h-6 rounded-full bg-white shadow-sm transition-all" />
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => onQtdChange(a.id, Math.max(0, qty - 1))}
                                            disabled={qty <= 0}
                                            className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center text-surface-600 hover:bg-surface-200 disabled:opacity-30 transition-colors"
                                        >
                                            <Minus size={14} />
                                        </button>
                                        <span className="w-8 text-center font-bold text-surface-900 text-sm">{qty}</span>
                                        <button
                                            onClick={() => onQtdChange(a.id, Math.min(a.max_quantidade, qty + 1))}
                                            disabled={qty >= a.max_quantidade}
                                            className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-700 hover:bg-primary-200 disabled:opacity-30 transition-colors"
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                            {custoExtra > 0 && (
                                <div className="flex items-center justify-end gap-1.5 text-xs font-bold text-primary-600">
                                    +{formatCurrency(custoExtra)}/mês
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function StepTerms({ termo, aceito, onAceitoChange, scrolledToBottom, onScroll, termsRef }: {
    termo: string; aceito: boolean; onAceitoChange: (v: boolean) => void
    scrolledToBottom: boolean; onScroll: (e: React.UIEvent<HTMLDivElement>) => void
    termsRef: React.RefObject<HTMLDivElement | null>
}) {
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-surface-700/70">
                <ScrollText size={18} className="text-primary-600" />
                <span>Leia o termo de aceite abaixo</span>
            </div>

            <div
                ref={termsRef}
                onScroll={onScroll}
                className="h-52 overflow-y-auto rounded-xl bg-surface-50 border border-surface-200 p-4 text-sm text-surface-700 leading-relaxed whitespace-pre-wrap font-mono text-xs"
            >
                {termo}
            </div>

            {!scrolledToBottom && (
                <p className="text-xs text-center text-surface-700/40 animate-pulse">
                    Role até o final para habilitar o aceite ↓
                </p>
            )}

            <label className={`flex items-start gap-3 p-4 rounded-xl border transition-all cursor-pointer
                ${aceito ? 'bg-success-50 border-success-200' : 'bg-white border-surface-200'}
                ${!scrolledToBottom ? 'opacity-50 pointer-events-none' : ''}`}>
                <input
                    type="checkbox"
                    checked={aceito}
                    onChange={(e) => onAceitoChange(e.target.checked)}
                    disabled={!scrolledToBottom}
                    className="w-4 h-4 mt-0.5 rounded border-surface-300 text-success-600 focus:ring-success-500"
                />
                <div>
                    <p className="text-sm font-medium text-surface-900">Li e aceito os termos e condições</p>
                    <p className="text-xs text-surface-700/60 mt-0.5">Declaro estar ciente de todas as condições descritas acima.</p>
                </div>
            </label>
        </div>
    )
}

function StepSummary({
    servico, valorAdesaoBase, valorAdesaoFinal, hasAdesao, hasAdesaoFinal,
    diaVencimento, selectedPlano, adicionais, adicionalQtds, crossSellOffers, valorMensalFinal
}: {
    servico: Servico; valorAdesaoBase: number; valorAdesaoFinal: number; hasAdesao: boolean; hasAdesaoFinal: boolean
    diaVencimento: number; selectedPlano: ServicoPlano | null; adicionais: ServicoAdicional[]; adicionalQtds: Record<string, number>
    crossSellOffers: { servico: Servico; valorComDesconto: number }[]; valorMensalFinal: number
}) {
    const adicionaisAtivos = adicionais.filter(a => (adicionalQtds[a.id] || 0) > 0)

    return (
        <div className="space-y-4">
            <div className="rounded-xl bg-surface-50 border border-surface-100 divide-y divide-surface-100 overflow-hidden">
                <div className="p-4 flex items-center justify-between">
                    <span className="text-sm text-surface-700/70">Serviço</span>
                    <span className="font-semibold text-surface-900">{servico.nome}</span>
                </div>
                {selectedPlano && (
                    <div className="p-4 flex items-center justify-between bg-indigo-50/50">
                        <span className="text-sm text-indigo-700 flex items-center gap-1.5">
                            <Layers size={14} /> Plano
                        </span>
                        <span className="font-bold text-indigo-700">{selectedPlano.nome} — {formatCurrency(selectedPlano.valor_mensal)}</span>
                    </div>
                )}
                {adicionaisAtivos.length > 0 && adicionaisAtivos.map(a => {
                    const qty = adicionalQtds[a.id] || 0
                    const cobrado = Math.max(0, qty - a.incluso_no_plano)
                    const adesaoDesc = (a.valor_adesao || 0) > 0 ? ` +${formatCurrency(a.valor_adesao! * qty)} adesão` : ''
                    return (
                        <div key={a.id} className="p-4 flex items-center justify-between bg-teal-50/30">
                            <span className="text-sm text-teal-700 flex items-center gap-1.5">
                                <ListPlus size={14} /> {a.nome} ×{qty}
                            </span>
                            <span className="font-bold text-teal-700">
                                {cobrado > 0 ? `+${formatCurrency(cobrado * a.valor_unitario)}` : 'Incluso'}
                                {adesaoDesc && <span className="text-xs font-normal"> / {adesaoDesc}</span>}
                            </span>
                        </div>
                    )
                })}
                {crossSellOffers.length > 0 && crossSellOffers.map(o => {
                    const cfg = o.servico.config_contratacao
                    const adesao = cfg?.gerar_adesao ? (cfg.valor_adesao_fixo || o.servico.valor_mensal) : 0
                    const adesaoDesc = adesao > 0 ? ` +${formatCurrency(adesao)} adesão` : ''
                    return (
                        <div key={o.servico.id} className="p-4 flex items-center justify-between bg-cyan-50/50">
                            <span className="text-sm text-cyan-700 flex items-center gap-1.5">
                                <ShoppingBag size={14} /> {o.servico.nome}
                            </span>
                            <span className="font-bold text-cyan-700">
                                +{formatCurrency(o.valorComDesconto)}/mês
                                {adesaoDesc && <span className="text-xs font-normal text-cyan-600/80"> / {adesaoDesc}</span>}
                            </span>
                        </div>
                    )
                })}
                <div className="p-4 flex items-center justify-between">
                    <span className="text-sm text-surface-700/70">Mensalidade Total</span>
                    <span className="font-bold text-primary-600 border-b border-primary-200">{formatCurrency(valorMensalFinal)}</span>
                </div>
                <div className="p-4 flex items-center justify-between">
                    <span className="text-sm text-surface-700/70">Vencimento</span>
                    <span className="text-sm font-bold text-surface-900 border-b border-primary-200">Dia {diaVencimento} de cada mês</span>
                </div>
                {hasAdesaoFinal && (
                    <div className="p-4 flex items-center justify-between bg-amber-50">
                        <span className="text-sm text-amber-800">
                            {servico.config_contratacao?.adesao_pro_rata ? 'Adesão Total (pro-rata base)' : 'Adesão Total'}
                        </span>
                        <span className="font-bold text-amber-700 text-lg">{formatCurrency(valorAdesaoFinal)}</span>
                    </div>
                )}
                {!!servico.pontos_adesao && servico.pontos_adesao > 0 && (
                    <div className="p-4 flex items-center justify-between bg-rose-50">
                        <span className="text-sm text-rose-700 flex items-center gap-1.5">
                            <Heart size={14} className="fill-current text-rose-500" /> Sempre Juntos
                        </span>
                        <span className="font-black text-rose-600">+{servico.pontos_adesao} PTS</span>
                    </div>
                )}
            </div>

            {hasAdesaoFinal && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                    <CreditCard size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-800">
                        Será gerada uma cobrança de adesão total de <strong>{formatCurrency(valorAdesaoFinal)}</strong>.
                        Os serviços serão ativados após a confirmação do pagamento.
                    </p>
                </div>
            )}

            {!hasAdesaoFinal && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-success-50 border border-success-100">
                    <CheckCircle2 size={16} className="text-success-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-success-800">
                        O serviço será ativado imediatamente após a confirmação e aceite do termo.
                    </p>
                </div>
            )}
        </div>
    )
}

function StepResult({ servico, hasAdesaoFinal, valorAdesaoFinal, success }: {
    servico: Servico; hasAdesaoFinal: boolean; valorAdesaoFinal: number; success: boolean
}) {
    if (!success) {
        return (
            <div className="flex flex-col items-center py-8 text-center">
                <Loader2 size={40} className="animate-spin text-primary-600 mb-4" />
                <p className="text-surface-700/60">Processando contratação...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col items-center py-5 text-center space-y-6 max-w-sm mx-auto">
            <div className="w-20 h-20 rounded-full bg-success-100 flex items-center justify-center -mb-2 shadow-inner">
                <CheckCircle2 size={44} className="text-success-600" />
            </div>

            <div className="space-y-1.5">
                <h3 className="text-2xl font-black text-surface-900 tracking-tight">
                    {hasAdesaoFinal ? 'Adesão Gerada com Sucesso!' : 'Contratação Concluída!'}
                </h3>
                <p className="text-sm text-surface-500 leading-relaxed">
                    {hasAdesaoFinal
                        ? `Os serviços foram vinculados ao seu perfil e serão ativados assim que a taxa de adesão for compensada.`
                        : `O serviço "${servico.nome}" foi ativado imediatamente em sua conta!`}
                </p>
            </div>

            {hasAdesaoFinal && (
                <div className="w-full rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50/50 border border-amber-200/60 p-5 space-y-4 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-5">
                         <CreditCard size={100} />
                    </div>
                    <div className="flex flex-col text-left space-y-1 relative z-10">
                        <span className="text-xs uppercase tracking-wider font-bold text-amber-800/60">Boleto / Pix de Adesão</span>
                        <span className="text-3xl font-black text-amber-600">{formatCurrency(valorAdesaoFinal)}</span>
                    </div>
                    <div className="flex bg-white/60 p-3 rounded-xl border border-white items-center gap-3 relative z-10">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-600">
                            <Info size={16} />
                        </div>
                        <p className="text-xs text-amber-900/80 text-left">
                            Acesse a aba <strong>Faturas</strong> no seu painel para visualizar o QRCode ou código PIX copia e cola.
                        </p>
                    </div>
                </div>
            )}

            {!hasAdesaoFinal && (
                <div className="w-full rounded-2xl bg-success-50 border border-success-200/60 p-4">
                    <p className="text-sm text-success-800 font-medium">
                        Sua primeira mensalidade vencerá no próximo dia de pagamento escolhido.
                    </p>
                </div>
            )}

            {!!servico.pontos_adesao && servico.pontos_adesao > 0 && (
                <div className="w-full rounded-2xl bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200/60 p-4 flex items-center gap-4 text-left">
                    <div className="w-12 h-12 rounded-full bg-rose-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Heart size={24} className="text-white fill-current" />
                    </div>
                    <div>
                        <p className="text-rose-800 font-bold mb-0.5">Sempre Juntos: +{servico.pontos_adesao} pontos!</p>
                        <p className="text-xs text-rose-600/90 leading-tight">Os pontos foram creditados automaticamente na sua carteira de fidelidade.</p>
                    </div>
                </div>
            )}
        </div>
    )
}

function StepCrossSell({ offers, selected, onToggle }: {
    offers: { servico: Servico; item: CrossSellItem; valorComDesconto: number; temDesconto: boolean }[]
    selected: string[]
    onToggle: (id: string) => void
}) {
    return (
        <div className="space-y-4">
            <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-4 flex items-start gap-3">
                <ShoppingBag className="text-cyan-600 mt-0.5 flex-shrink-0" size={20} />
                <div className="space-y-1">
                    <h4 className="text-sm font-bold text-cyan-800">Aproveite as ofertas e contrate junto!</h4>
                    <p className="text-xs text-cyan-700/80">
                        Adicione esses serviços agora mesmo em um único contrato e aproveite descontos exclusivos que só aparecem aqui.
                    </p>
                </div>
            </div>

            <div className="space-y-3">
                {offers.map(o => {
                    const isSelected = selected.includes(o.servico.id)
                    const cfg = o.servico.config_contratacao
                    const adesao = cfg?.gerar_adesao ? (cfg.valor_adesao_fixo || o.servico.valor_mensal) : 0
                    
                    return (
                        <div 
                            key={o.servico.id} 
                            onClick={() => onToggle(o.servico.id)}
                            className={`p-4 rounded-xl border-2 transition-all cursor-pointer relative overflow-hidden
                                ${isSelected ? 'border-primary-500 bg-primary-50/50' : 'border-surface-200 bg-white hover:border-primary-300'}`}
                        >
                            {o.temDesconto && (
                                <div className="absolute top-0 right-0 bg-red-500 text-white px-2 py-0.5 rounded-bl-xl text-[10px] font-black tracking-wider shadow-sm flex items-center gap-1">
                                    <Tag size={10} /> 
                                    {o.item.desconto_percentual ? `-${o.item.desconto_percentual}%` : `-${formatCurrency(o.item.desconto_fixo || 0)}`}
                                </div>
                            )}

                            <div className="flex gap-4">
                                <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5
                                    ${isSelected ? 'bg-primary-600' : 'border-2 border-surface-300'}`}>
                                    {isSelected && <CheckCircle2 size={16} className="text-white" />}
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs font-bold text-cyan-600 uppercase tracking-wider mb-0.5">
                                        {o.item.titulo_oferta || 'Oferta Adicional'}
                                    </p>
                                    <h4 className="text-base font-bold text-surface-900 leading-tight">
                                        {o.servico.nome}
                                    </h4>
                                    {o.servico.descricao && (
                                        <p className="text-xs text-surface-500 mt-1 line-clamp-2">
                                            {o.servico.descricao}
                                        </p>
                                    )}
                                    <div className="mt-3 bg-white/60 p-2.5 rounded-lg border border-surface-100 flex justify-between items-center">
                                        <span className="text-xs font-medium text-surface-500">Adicional na mensalidade:</span>
                                        <div className="text-right">
                                            {o.temDesconto && (
                                                <span className="text-xs text-surface-400 line-through mr-2">{formatCurrency(o.servico.valor_mensal)}</span>
                                            )}
                                            <span className="text-sm font-black text-primary-700">{formatCurrency(o.valorComDesconto)}<span className="text-[10px] font-medium text-surface-500">/mês</span></span>
                                        </div>
                                    </div>
                                    {adesao > 0 && (
                                        <p className="text-[10px] text-amber-700/80 mt-1.5 text-right font-medium">
                                            + {formatCurrency(adesao)} (Taxa de Adesão)
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
